/**
 * Account Checkout Success Page
 * Displayed after successful Stripe payment/setup
 */
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { CheckCircle, Clock, Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../config/api';

const AccountCheckoutSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [confirming, setConfirming] = useState(true);
  const [error, setError] = useState(null);

  // Check if this is a scheduled change (no payment to confirm)
  const isScheduled = location.state?.scheduled || false;
  const scheduledMessage = location.state?.message;
  const scheduledPlanName = location.state?.planName;

  // Get credentials from session storage
  const email = sessionStorage.getItem('account_email') || '';
  const pwd = sessionStorage.getItem('account_pwd') || '';

  useEffect(() => {
    if (isScheduled) {
      // No payment to confirm for scheduled changes
      setConfirming(false);
    } else {
      confirmPayment();
    }
  }, []);

  const confirmPayment = async () => {
    try {
      // Call confirm payment API
      const response = await fetch(`${API_BASE_URL}/general/getgeneral`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: 'confirm',
          email: email,
          pwd: pwd
        })
      });

      const result = await response.json();

      if (!result.success) {
        console.warn('Confirm payment response:', result.message);
        // Don't show error - webhook might handle it
      }
    } catch (err) {
      console.error('Error confirming payment:', err);
      // Don't show error - webhook might handle it
    } finally {
      setConfirming(false);
    }
  };

  const handleGoToDashboard = () => {
    navigate('/app/account/dashboard');
  };

  return (
    <div className="min-h-screen bg-dark flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        className="max-w-md w-full bg-dark-100 rounded-2xl p-8 border border-white/10 text-center"
      >
        {confirming ? (
          <>
            <Loader2 className="w-16 h-16 text-accent mx-auto animate-spin mb-6" />
            <h1 className="text-2xl font-bold text-white mb-2">Processing...</h1>
            <p className="text-gray-400">Please wait while we confirm your subscription.</p>
          </>
        ) : isScheduled ? (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <Clock className="w-20 h-20 text-blue-400 mx-auto mb-6" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-white mb-2">Downgrade Scheduled</h1>
            <p className="text-gray-400 mb-8">
              {scheduledMessage || `Your plan will change to ${scheduledPlanName || 'the selected plan'} at the end of your current billing period. You'll keep your current features until then.`}
            </p>

            <button
              onClick={handleGoToDashboard}
              className="w-full py-4 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all"
            >
              Go to Dashboard
            </button>
          </>
        ) : (
          <>
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <CheckCircle className="w-20 h-20 text-accent mx-auto mb-6" />
            </motion.div>
            
            <h1 className="text-2xl font-bold text-white mb-2">Subscription Successful!</h1>
            <p className="text-gray-400 mb-8">
              Your subscription has been set up successfully. You can now enjoy all the features of your plan.
            </p>

            <button
              onClick={handleGoToDashboard}
              className="w-full py-4 bg-gradient-to-r from-accent to-accent-dark text-dark font-semibold rounded-xl hover:shadow-lg hover:shadow-accent/30 transition-all"
            >
              Go to Dashboard
            </button>
          </>
        )}
      </motion.div>
    </div>
  );
};

export default AccountCheckoutSuccess;
